import { FC, useEffect, useState } from "react";
import {
  EventEntryInput,
  EventStatus,
  EventType,
  RoadwayDirection,
} from "../../../reducers/states/event-management";
import { getEventsRequest } from "../../../actions";
import { useSelector } from "react-redux";
import { IRootReducer } from "../../../reducers";
import { useDispatch } from "react-redux";
import {
  DataGrid,
  GridColDef,
  GridOverlay,
  GridRowSelectionModel,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import moment from "moment";
import { Box } from "@mui/material";
import EventDetailModal from "../event-detail-modal";

export interface EventsGridRow {
  id: string;
  endDate?: string;
  eventName: string;
  type: EventType;
  startDate?: string;
  status: EventStatus;
  stateRoute: string;
  endMilepost: number;
  startMilepost: number;
  direction: RoadwayDirection;
  commonName: string;
  contractNumber: string;
}

const EventList: FC = () => {
  const dispatch = useDispatch();
  const [events, setEvents] = useState<EventsGridRow[] | undefined>(undefined);
  const [pagination, setPagination] = useState({ pageSize: 50, page: 1 });
  const [isGridLoading, setIsGridLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState<EventEntryInput | undefined>(undefined);

  const eventsStore = useSelector((state: IRootReducer) => state.eventManagementReducer.events);

  useEffect(() => {
    dispatch(getEventsRequest());
  }, [dispatch]);

  // Update gridData from latest dispatch events loaded
  useEffect(() => {
    setIsGridLoading(true);

    if (eventsStore?.length) {
      const gridRows = eventsStore?.map((value, index, array) => {
        const location = value.locations[0];
        return {
          id: value.id,
          eventName: value.eventName,
          type: value.type,
          status: value.status,
          endMilepost: location?.endMilepost ?? "",
          startMilepost: location?.startMilepost ?? "",
          commonName: location?.commonName ?? "",
          direction: location?.direction ?? "",
          stateRoute: location?.stateRoute ?? "",
          contractNumber: value.contractNumber,
          startDate: value.startDate,
          endDate: value.endDate,
        } as EventsGridRow;
      });

      setEvents(gridRows);
    } else {
      setEvents([] as EventsGridRow[]);
    }

    setIsGridLoading(false);
  }, [eventsStore]);

  const handleSelectionModelChange = (rowIds: GridRowSelectionModel) => {
    const selectedEvents: EventsGridRow[] = events?.filter((e) => rowIds.includes(e.id)).map((s) => s) || [];

    const eventId = selectedEvents[0]?.id;

    const projInfo = eventId
      ? eventsStore?.find((info) => {
          return eventId === info.id;
        })
      : undefined;

    setSelectedEvent(projInfo);
  };

  const columns: GridColDef[] = [
    {
      field: "eventName",
      headerName: "Name",
      flex: 1.0,
      minWidth: 200,
    },
    {
      field: "type",
      headerName: "Event Type",
      flex: 1.0,
      minWidth: 150,
    },
    {
      field: "status",
      headerName: "Status",
    },
    {
      field: "stateRoute",
      headerName: "State Route",
    },
    {
      field: "direction",
      headerName: "Direction",
      flex: 0.75,
      minWidth: 150,
    },
    {
      field: "mileposts",
      headerName: "Mileposts",
      valueGetter: (params: GridValueGetterParams) => {
        return `${params.row.startMilepost} - ${params.row.endMilepost}`;
      },
    },
    {
      field: "commonName",
      headerName: "Common Name",
      valueGetter: (params) => {
        return params.row.commonName || "";
      },
      flex: 1.0,
      minWidth: 150,
    },
    {
      field: "contractNumber",
      headerName: "Contract No.",
    },
    {
      field: "startDate",
      headerName: "Start Date",
      type: "date-time",
      renderCell: (params) => {
        return moment(params.row.startDate as string).format("M/D/YY");
      },
    },
    {
      field: "endDate",
      headerName: "End Date",
      type: "date-time",
      renderCell: (params) => {
        return moment(params.row.endDate as string).format("M/D/YY");
      },
    },
  ];

  const Toolbar = (): JSX.Element => {
    return (
      <GridToolbarContainer>
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
      </GridToolbarContainer>
    );
  };

  const getGridHeight = () => {
    const headerHeight = 64;
    const appMainBoxMargins = 16;
    const title = 53;
    const total = headerHeight + appMainBoxMargins + title;
    return `calc(100vh - ${total}px`;
  }

  return (
    <> 
      <Box style={{ height: getGridHeight() }}>
        <DataGrid
          columns={columns}
          rows={events || []}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          loading={isGridLoading}
          density="compact"
          slots={{
            toolbar: Toolbar,
            noRowsOverlay: () => {
              return <GridOverlay>No events to display</GridOverlay>;
            },
            noResultsOverlay: () => {
              return <GridOverlay>No events match current filers</GridOverlay>;
            },
          }}
          onRowSelectionModelChange={(selection: GridRowSelectionModel) => {
            handleSelectionModelChange(selection);
          }}
        />
      </Box>
      {selectedEvent && (
        <EventDetailModal
          props={{
            isOpen: true,
            event: selectedEvent,
            onClose: () => setSelectedEvent(undefined),
          }}
        />
      )}
    </>
  );
};

export default EventList;
